import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule, DecimalPipe } from '@angular/common';
import { CellRenderer, CellRendererParams } from '../../core/interfaces/cell-renderer.interface';
import { UnitOfMeasurementComponent } from 'app/modules/components/master/unit-of-measurement/unit-of-measurement.component';
import { UnitMeasureDisplayComponent } from 'app/shared/components/unit-measure-display/unit-measure-display.component';

/**
 * Delivered Quantity Cell Renderer Component
 *
 * Cell renderer for "Cantidad Entregado/Facturado" in loss reports.
 * Shows delivered/invoiced quantity with base and parallel units.
 * Reads from sourceTransaction.nextTransaction fields.
 *
 * Usage in ColDef:
 * ```typescript
 * {
 *   field: 'sourceTransaction.nextTransaction.soldQuantity',
 *   headerName: 'Cantidad Entregado/Facturado',
 *   cellRenderer: DeliveredQuantityCellRendererComponent,
 *   cellRendererParams: {
 *     getUMPActive: () => this.UMPActive
 *   }
 * }
 * ```
 */
@Component({
  selector: "app-delivered-quantity-cell-renderer",
  standalone: true,
  imports: [CommonModule, DecimalPipe, UnitMeasureDisplayComponent],
  template: `
    <app-unit-measure-display
      [usedUnit]="{
        quantity: params?.data?.sourceTransaction?.soldQuantity,
        unitId: params?.data?.sourceTransaction?.soldUnitOfMeasure?._id,
        unitName: params?.data?.sourceTransaction?.soldUnitOfMeasure?.name,
        unitAbbreviation: params?.data?.sourceTransaction?.soldUnitOfMeasure?.code,
      }"
      [baseUnit]="{
        quantity: params?.data?.sourceTransaction?.baseQuantity,
        unitId: params?.data?.sourceTransaction?.baseUnitOfMeasure?._id,
        unitName: params?.data?.sourceTransaction?.baseUnitOfMeasure?.name,
        unitAbbreviation: params?.data?.sourceTransaction?.baseUnitOfMeasure?.code,
      }"
      [parallelUnit]="{
        quantity: params?.data?.sourceTransaction?.pararellQuantity,
        unitId: params?.data?.sourceTransaction?.pararellUnitOfMeasure?._id,
        unitName: params?.data?.sourceTransaction?.pararellUnitOfMeasure?.name,
        unitAbbreviation: params?.data?.sourceTransaction?.pararellUnitOfMeasure?.code,
      }"
      [material]="{
        _id: params?.data?.sourceTransaction?.material?._id,
        code: params?.data?.sourceTransaction?.material?.code,
        UMPActive: params?.data?.sourceTransaction?.material?.UMPActive,
      }"
      [companyUMPActive]="isUMPActive"
      [statusId]="params.statusId"
    ></app-unit-measure-display>
  `,
  styles: [
    `
      :host {
        display: block;
        width: 100%;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DeliveredQuantityCellRendererComponent
  implements CellRenderer<any, any>
{
  @Input() params!: CellRendererParams<any, any>;

  private readonly decimalPipe = new DecimalPipe("en-US");

  get isUMPActive(): boolean {
    if (this.params?.getUMPActive) {
      return this.params.getUMPActive();
    }
    return false;
  }

  get soldQuantity(): string {
    const value =
      this.params?.data?.sourceTransaction?.nextTransaction?.soldQuantity;
    return this.decimalPipe.transform(value, "1.3-3") ?? "0";
  }

  get soldUnitName(): string {
    return (
      this.params?.data?.sourceTransaction?.nextTransaction?.soldUnitOfMeasure
        ?.name ?? "-"
    );
  }

  get baseQuantity(): string {
    const value =
      this.params?.data?.sourceTransaction?.nextTransaction?.baseQuantity;
    return this.decimalPipe.transform(value, "1.3-3") ?? "0";
  }

  get baseUnitName(): string {
    return (
      this.params?.data?.sourceTransaction?.nextTransaction?.baseUnitOfMeasure
        ?.name ?? "-"
    );
  }

  get hasParallelUnit(): boolean {
    return !!this.params?.data?.sourceTransaction?.nextTransaction
      ?.pararellUnitOfMeasure;
  }

  get parallelQuantity(): string {
    const value =
      this.params?.data?.sourceTransaction?.nextTransaction?.pararellQuantity;
    return this.decimalPipe.transform(value, "1.3-3") ?? "0";
  }

  get parallelUnitName(): string {
    return (
      this.params?.data?.sourceTransaction?.nextTransaction
        ?.pararellUnitOfMeasure?.name ?? "-"
    );
  }
}
